import type { DomainDto } from "@bafe/api-client";
import { GlobeIcon, PlusIcon } from "@phosphor-icons/react";
import { Card, DataTable, EmptyState, PageHeader, StatusPill, type Column } from "@bafe/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { LinkButton } from "../../components/LinkButton";
import { QueryState } from "../../components/QueryState";
import { api } from "../../lib/api";
import { formatCount, formatRelative } from "../../lib/format";
import { queryKeys } from "../../lib/query";
import { DOMAIN_STATUS } from "../../lib/status";

const columns: Column<DomainDto>[] = [
	{
		id: "domain",
		header: "Domain",
		primary: true,
		className: "first:pl-5",
		render: (domain) => (
			<Link
				to={`/manage/domains/${domain.id}`}
				className="font-medium text-(--text-primary) underline-offset-2 hover:underline"
			>
				{domain.domainName}
			</Link>
		),
	},
	{
		id: "purpose",
		header: "Purpose",
		render: (domain) => {
			const parts = [
				domain.emailEnabled ? "Email" : null,
				domain.sitesEnabled ? "Website" : null,
				domain.platformApp === "hub" ? "Hub" : null,
				domain.platformApp === "webmail" ? "Webmail" : null,
			].filter(Boolean);
			return parts.join(" · ") || "—";
		},
	},
	{
		id: "status",
		header: "Status",
		render: (domain) => (
			<StatusPill tone={DOMAIN_STATUS[domain.status].tone}>{DOMAIN_STATUS[domain.status].label}</StatusPill>
		),
	},
	{
		id: "mailboxes",
		header: "Email accounts",
		render: (domain) =>
			domain.emailEnabled ? formatCount(domain.mailboxCount, "email account", "email accounts") : "—",
	},
	{
		id: "checked",
		header: "Last checked",
		render: (domain) => formatRelative(domain.lastDnsCheckAt),
	},
	{
		id: "actions",
		header: "Actions",
		align: "right",
		className: "whitespace-nowrap last:pr-5",
		render: (domain) => (
			<span className="inline-flex gap-3">
				<Link
					to={`/manage/domains/${domain.id}`}
					className="text-sm font-medium text-(--color-brand-700) underline underline-offset-2"
				>
					Manage<span className="sr-only"> {domain.domainName}</span>
				</Link>
				<Link
					to={`/manage/domains/${domain.id}?tab=dns`}
					className="text-sm font-medium text-(--color-brand-700) underline underline-offset-2"
				>
					DNS setup<span className="sr-only"> for {domain.domainName}</span>
				</Link>
			</span>
		),
	},
];

export function DomainsList() {
	const domains = useQuery({ queryKey: queryKeys.domains, queryFn: () => api.listDomains() });

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title="Domains"
				description="Hostnames you use for email, websites, Hub or Webmail."
				action={
					<LinkButton
						to="/manage/domains/new"
						variant="primary"
						iconLeft={<PlusIcon className="size-4" aria-hidden="true" />}
					>
						Add domain
					</LinkButton>
				}
			/>

			<QueryState query={domains} errorTitle="We could not load your domains.">
				{(rows) => (
					<Card flush>
						<DataTable
							caption="Your domains"
							columns={columns}
							rows={rows}
							rowKey={(domain) => domain.id}
							empty={
								<EmptyState
									icon={<GlobeIcon className="size-8" aria-hidden="true" />}
									title="No domains yet"
									description="Add a hostname you own, such as yourcompany.com or hub.yourcompany.com. Key walks you through the DNS changes."
									action={
										<LinkButton to="/manage/domains/new" variant="primary">
											Add your first domain
										</LinkButton>
									}
								/>
							}
						/>
					</Card>
				)}
			</QueryState>
		</div>
	);
}
